<script lang="ts" setup>
import { notify } from '@kyvg/vue3-notification'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import useForm from '@/hooks/use-form'
import { useAuthStore } from '@/stores/auth-store'
import { uuid } from '@/types/general'
import { CompanyDetailsFieldType, companyDetailsFieldLabels } from '@/types/pod'
import { emitter } from '@/utils/mitt'

import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyTextarea from '@/components/my-components/form/MyTextarea.vue'

export interface Props {
    modelValue: boolean
    transactionId?: uuid
    receiptId?: uuid
    initialValue?: string | null
    fieldType: CompanyDetailsFieldType
}

const props = withDefaults(defineProps<Props>(), {
    initialValue: '',
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'close'): void
}>()

const authStore = useAuthStore()
const { t } = useI18n()

const form = useForm({
    [props.fieldType]: props.initialValue ?? '',
})

const fieldLabel = computed(() => t(companyDetailsFieldLabels[props.fieldType]))

async function onSubmit() {
    const transactionRoute = window.route('packaging.company.transactions.company-details.update', {
        company: authStore.companyId,
        transaction: props.transactionId ?? '',
    })

    const receiptRoute = window.route('pod.company.receipts.company-details.update', {
        company: authStore.companyId,
        receipt: props.receiptId ?? '',
    })

    const response = await form.submit('put', props.transactionId ? transactionRoute : receiptRoute)

    if (response) {
        emit('update:modelValue', false)
        emit('close')
        props.transactionId ? emitter.emit('fetchTransactions') : emitter.emit('fetchReceipts')
        notify({ type: 'success', text: t('noteUpdated', { field: fieldLabel.value }) })
    }
}

watch(
    () => props.modelValue,
    () => form.reset({ [props.fieldType]: props.initialValue ?? '' }),
)
</script>

<template>
    <MyModal :value="props.modelValue" @close="emit('update:modelValue', false)">
        <template #title>
            {{ t('updateEntity', { entity: fieldLabel }) }}
        </template>

        <LoaderWrapper :visible="form.loading.value" />

        <p class="mt-2 mb-4" v-text="t('noteIsPrivate', { field: fieldLabel })" />

        <MyForm class="space-y-4" :errors="form.errors.value" @submit.prevent="onSubmit">
            <MyTextarea
                v-model="form.data[props.fieldType]"
                :name="props.fieldType"
                :label="fieldLabel"
                autofocus
            />

            <div class="flex justify-end space-x-3">
                <MyButton type="button" @click="emit('update:modelValue', false)">
                    {{ t('cancel') }}
                </MyButton>

                <MyButton :disabled="form.loading.value" scheme="primary">
                    {{ t('save') }}
                </MyButton>
            </div>
        </MyForm>
    </MyModal>
</template>
